import React, { useState } from 'react';
import { payFor } from '../giftcard-shop/shopUtils';

// The interactive guts of choosing a voucher amount: denomination pills, an
// optional custom amount within range, the promo "client pays" summary and the
// Add button. Reused by both presentations — the POS bottom SHEET
// (VoucherAmountPicker) and the diary-checkout INLINE step — so the maths and
// UI stay identical. onConfirm({ faceValue, charge }).

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function VoucherAmountFields({ voucher, currency = '£', onConfirm }) {
  const denominations = (voucher.denominations && voucher.denominations.length)
    ? voucher.denominations
    : (voucher.value != null ? [Number(voucher.value)] : []);
  const allowsCustom = !!voucher.customAmount;

  const [mode, setMode] = useState(denominations.length ? 'denom' : 'custom');
  const [selectedDenom, setSelectedDenom] = useState(denominations[0] != null ? denominations[0] : null);
  const [customValue, setCustomValue] = useState('');
  const [error, setError] = useState('');

  const faceValue = mode === 'custom' ? parseFloat(customValue) : selectedDenom;
  const validFace = Number.isFinite(faceValue) && faceValue > 0;
  const charge = validFace ? payFor(voucher, faceValue) : 0;
  const hasPromo = validFace && charge < faceValue;

  const customMin = voucher.customMin != null ? Number(voucher.customMin) : 0;
  const customMax = voucher.customMax != null ? Number(voucher.customMax) : Infinity;

  const promoActive = Number(voucher.promoValue) > 0 && (voucher.promoMode === 'fixed' || voucher.promoMode === 'percent');
  const promoLabel = !promoActive ? null
    : voucher.promoMode === 'fixed' ? `${currency}${voucher.promoValue} off` : `${voucher.promoValue}% off`;

  const confirm = () => {
    if (!validFace) { setError('Choose an amount'); return; }
    if (mode === 'custom' && (faceValue < customMin || faceValue > customMax)) {
      setError(`Amount must be between ${currency}${customMin} and ${currency}${Number.isFinite(customMax) ? customMax : '∞'}`);
      return;
    }
    onConfirm({ faceValue: round2(faceValue), charge: round2(payFor(voucher, faceValue)) });
  };

  return (
    <>
      <p style={{ fontSize: 13, color: '#8e8e93', margin: '0 0 18px' }}>
        Choose an amount{promoLabel ? ` (${promoLabel} promo)` : ''}
      </p>

      {denominations.length > 0 && (
        <>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 8px' }}>Denominations</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: allowsCustom ? 16 : 20 }}>
            {denominations.map((d) => {
              const active = mode === 'denom' && Number(selectedDenom) === Number(d);
              const dCharge = payFor(voucher, d);
              return (
                <button
                  key={d}
                  onClick={() => { setMode('denom'); setSelectedDenom(d); setError(''); }}
                  style={{
                    padding: '10px 16px', borderRadius: 100, cursor: 'pointer',
                    border: active ? '2px solid #007EA0' : '1px solid #DEDEE2',
                    background: active ? '#E6F4F7' : '#fff',
                    fontWeight: 700, fontSize: 15, color: '#1a1a1a',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 64
                  }}
                >
                  <span>{currency}{d}</span>
                  {dCharge < d && <span style={{ fontSize: 11, fontWeight: 600, color: '#007EA0' }}>pay {currency}{dCharge}</span>}
                </button>
              );
            })}
          </div>
        </>
      )}

      {allowsCustom && (
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 8px' }}>Custom amount</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: mode === 'custom' ? '2px solid #007EA0' : '1px solid #DEDEE2', borderRadius: 10, padding: '10px 14px' }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}>{currency}</span>
            <input
              type="number" inputMode="decimal" min={customMin} max={Number.isFinite(customMax) ? customMax : undefined}
              value={customValue}
              onFocus={() => setMode('custom')}
              onChange={(e) => { setMode('custom'); setCustomValue(e.target.value); setError(''); }}
              placeholder={`${customMin || 0}`}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 18, fontWeight: 700, color: '#1a1a1a', background: 'transparent', width: '100%' }}
            />
          </div>
          {Number.isFinite(customMax) && (
            <p style={{ fontSize: 11, color: '#9ca3af', margin: '6px 2px 0' }}>Between {currency}{customMin} and {currency}{customMax}</p>
          )}
        </div>
      )}

      {validFace && (
        <div style={{ background: '#F7F7F7', borderRadius: 10, padding: '12px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: 12, color: '#8e8e93', margin: 0 }}>Client pays</p>
            <p style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', margin: '2px 0 0' }}>{currency}{round2(charge)}</p>
          </div>
          {hasPromo && (
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 12, color: '#8e8e93', margin: 0 }}>Face value</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#007EA0', margin: '2px 0 0' }}>{currency}{round2(faceValue)}</p>
            </div>
          )}
        </div>
      )}

      {error && <p style={{ fontSize: 12.5, color: '#B14438', margin: '0 0 12px' }}>{error}</p>}

      <button
        onClick={confirm}
        disabled={!validFace}
        style={{
          width: '100%', padding: '15px', borderRadius: 12, border: 'none', cursor: validFace ? 'pointer' : 'not-allowed',
          background: validFace ? '#29282D' : '#C7C7CC', color: '#fff', fontSize: 15, fontWeight: 600
        }}
      >
        Add {validFace ? `${currency}${round2(charge)}` : ''}
      </button>
    </>
  );
}
